import { Component, ViewChild, OnInit } from '@angular/core';
import { catchError, tap } from 'rxjs/operators';
import { FeedbackType } from 'src/app/general-components/feedback/feedback-type';
import { FeedbackSettings } from 'src/app/general-components/feedback/feedback-settings';
import { Router } from '@angular/router';
import { FeedbackComponent } from 'src/app/general-components/feedback/feedback.component';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorResponse } from 'src/app/interfaces/bad-request';
import { concat, EMPTY, Observable } from 'rxjs';
import { ReservedSpecialSku } from '../reserved-special-sku';
import { SpecialsService } from '../specials.service';
import { EmptyStats, Stats } from '../suppliers/supplier';
import { EmptyINDISpecial, INDISpecial } from './indi-special';
import { INDISpecialsTableComponent } from './indi-specials-table/indi-specials-table.component';
import { NewINDISpecialComponent } from './new-indi-special/new-indi-special.component';
import { INDICombinedData, INDISpecialsService } from './indi-specials.service';

@Component({
  selector: 'app-indi-specials',
  templateUrl: './indi-specials.component.html',
  styleUrls: ['./indi-specials.component.scss'],
})
export class INDISpecialsComponent implements OnInit {
  @ViewChild(NewINDISpecialComponent)
  newINDISpecialComponent!: NewINDISpecialComponent;
  @ViewChild(INDISpecialsTableComponent)
  INDISpecialsTableComponent!: INDISpecialsTableComponent;
  @ViewChild(FeedbackComponent)
  feedbackComponent!: FeedbackComponent;
  specials: INDISpecial[] = [];
  supplierNames: string[] = [];
  stats: Stats = EmptyStats();
  newSpecial: INDISpecial = EmptyINDISpecial();
  failedSpecial: INDISpecial | undefined;
  keepInputs: boolean = false;

  feedbackSettings: FeedbackSettings = {
    identifier: '',
    show: false,
    type: FeedbackType.error,
    message: 'Er is iets misgegaan sorry',
    closeButton: false,
  };
  newSpecialFeedbackIdentifier = 'newSpecialFeedback';
  generalFeedbackIdentifier = 'generalFeedback';

  reservedSpecialSku: ReservedSpecialSku | undefined;

  isLoading: boolean = false;

  constructor(
    private IndiSpecialsService: INDISpecialsService,
    private specialsService: SpecialsService,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Sequential on purpose: Firefox aborts parallel requests to the VPN-only API hosts,
    // see "Parallel requests to private API hosts" in AGENTS.md.
    concat(this.getReservedSpecialSku(), this.getINDISpecials()).subscribe();
  }

  getINDISpecials(): Observable<unknown> {
    this.isLoading = true;
    return this.IndiSpecialsService.getCombinedData().pipe(
      tap((combinedData: INDICombinedData) => {
        this.specials = combinedData.specials;
        this.supplierNames = this.collectSupplierNames();
        this.stats = combinedData.stats;
        this.isLoading = false;
      }),
      catchError((error: Error) => {
        this.updateFeedback(
          this.generalFeedbackIdentifier,
          FeedbackType.error,
          true,
          'Er is iets foutgegaan bij het ophalen van de INDI specials',
          'Terug naar hoofdmenu',
          false
        );
        console.error(error);
        this.isLoading = false;
        return EMPTY;
      })
    );
  }

  getReservedSpecialSku(): Observable<unknown> {
    this.isLoading = true;
    return this.specialsService.getReservedSpecialSku().pipe(
      tap((reservedSpecialSku: ReservedSpecialSku) => {
        this.reservedSpecialSku = reservedSpecialSku;
      }),
      catchError((error: Error) => {
        this.updateFeedback(
          this.generalFeedbackIdentifier,
          FeedbackType.error,
          true,
          'Er kon geen special gereserveerd worden, probeer het later nog een keer',
          'Terug naar hoofdmenu',
          false
        );
        this.isLoading = false;
        console.error(error);
        return EMPTY;
      })
    );
  }

  setKeepInput(keepInputs: boolean) {
    this.keepInputs = keepInputs;
  }

  createSpecial(special: INDISpecial) {
    this.isLoading = true;
    this.IndiSpecialsService.createINDISpecial(special).subscribe({
      next: (newSpecial: INDISpecial) => {
        this.specials.push(newSpecial);
        this.supplierNames = this.collectSupplierNames();

        this.INDISpecialsTableComponent.sortByField('creationDate', false);
        this.updateFeedback(
          this.newSpecialFeedbackIdentifier,
          FeedbackType.success,
          true,
          `De special ${newSpecial.sku} is aangemaakt!`,
          undefined,
          false
        );
        this.feedbackComponent.fadeOut(2000);
        this.getReservedSpecialSku().subscribe();
        if (!this.keepInputs) {
          this.newINDISpecialComponent.clearSpecial();
        } else {
          this.newINDISpecialComponent.clearUniqueFields();
        }
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.failedSpecial = special;
        console.error(error.error);
        if (error.status == 400) {
          const badRequestError = error.error as ErrorResponse;
          const errorException = badRequestError.exception;
          if (errorException?.includes('DoubleDescriptionException')) {
            const SKUs = this.getSKUsFromError(errorException);
            this.updateFeedback(
              this.newSpecialFeedbackIdentifier,
              FeedbackType.error,
              true,
              `Er bestaat al een special met de omschrijving "${special.description}": ${SKUs}`,
              'Aanpassen en opnieuw proberen',
              true
            );
          } else {
            this.updateFeedback(
              this.newSpecialFeedbackIdentifier,
              FeedbackType.error,
              true,
              'Er klopte *iets* niet aan de special',
              'Aanpassen en opnieuw proberen',
              true
            );
          }
        } else {
          this.updateFeedback(
            this.newSpecialFeedbackIdentifier,
            FeedbackType.error,
            true,
            'Er is iets fout gegaan bij aanmaken van de special',
            undefined,
            true
          );
        }

        this.isLoading = false;
      },
    });
  }

  collectSupplierNames(): string[] {
    const names = this.specials.map(special => special.indiSupplierName?.trim()).filter(name => !!name) as string[];
    return [...new Set(names)].sort((a, b) => a.localeCompare(b));
  }

  getSKUsFromError(errorException: string) {
    const parts = errorException.match(/\[(.*?)\]/);
    return parts ? parts[1] : 'Niet beschikbaar';
  }

  retryFailedSpecial() {
    if (this.failedSpecial) {
      this.feedbackSettings.show = false;
      this.failedSpecial.creationDate = '';

      this.newINDISpecialComponent.retryFailedSpecial(this.failedSpecial);
    }
  }

  updateFeedback(
    identifier: string,
    type: FeedbackType,
    show: boolean,
    message: string,
    buttonText: string | undefined,
    closeButton: boolean
  ) {
    this.feedbackSettings.identifier = identifier;
    this.feedbackSettings.type = type;
    this.feedbackSettings.show = show;
    this.feedbackSettings.message = message;
    this.feedbackSettings.buttonText = buttonText;
    this.feedbackSettings.closeButton = closeButton;
  }

  handleFeedbackOutput(identifier: string) {
    if (identifier == this.newSpecialFeedbackIdentifier) {
      this.retryFailedSpecial();
    } else {
      this.returnHome();
    }
  }

  returnHome() {
    void this.router.navigate(['home']);
  }

  feedbackType() {
    return FeedbackType;
  }
}
